import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  FaTimes, FaPlus, FaTrash, FaPencilAlt, FaCheck,
  FaArrowRight, FaSpinner,
} from 'react-icons/fa';
import api from '../services/api';

// ─── CSS ───────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&display=swap');

.cp-root { font-family: 'DM Sans', system-ui, sans-serif; }
.cp-root * { box-sizing: border-box; }

@keyframes cp-in {
  from { opacity:0; transform: scale(0.95) translateY(16px); }
  to   { opacity:1; transform: scale(1) translateY(0); }
}
@keyframes cp-form-in {
  from { opacity:0; transform:translateY(-6px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes cp-pulse { 50% { opacity:.5; } }

.cp-modal-box  { animation: cp-in 0.24s cubic-bezier(0.16,1,0.3,1) both; }
.cp-form-anim  { animation: cp-form-in 0.18s ease-out both; }

.cp-scroll::-webkit-scrollbar       { width:4px; }
.cp-scroll::-webkit-scrollbar-track { background:transparent; }
.cp-scroll::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.09); border-radius:4px; }
.cp-scroll::-webkit-scrollbar-thumb:hover { background:rgba(255,255,255,0.17); }

/* Slider */
.cp-slider {
  -webkit-appearance:none; appearance:none;
  width:100%; height:5px; border-radius:9999px; outline:none; cursor:pointer;
  background: linear-gradient(
    to right,
    var(--sc,#f59e0b) 0%, var(--sc,#f59e0b) var(--sp,50%),
    rgba(255,255,255,0.11) var(--sp,50%), rgba(255,255,255,0.11) 100%
  );
}
.cp-slider::-webkit-slider-thumb {
  -webkit-appearance:none;
  width:20px; height:20px; border-radius:50%;
  background: var(--sc,#f59e0b);
  border: 2.5px solid rgba(255,255,255,0.88);
  box-shadow: 0 0 0 4px var(--sg,rgba(245,158,11,0.22)), 0 2px 8px rgba(0,0,0,0.45);
  cursor:pointer; transition: transform .12s, box-shadow .12s;
}
.cp-slider::-webkit-slider-thumb:hover {
  transform: scale(1.25);
  box-shadow: 0 0 0 8px var(--sg,rgba(245,158,11,0.14)), 0 2px 8px rgba(0,0,0,0.45);
}
.cp-slider::-moz-range-thumb {
  width:20px; height:20px; border-radius:50%;
  background:var(--sc,#f59e0b); border:2.5px solid rgba(255,255,255,0.88);
}

/* Hover helpers */
.cp-vcard { transition:transform .14s,border-color .14s,background .14s; cursor:pointer; position:relative; }
.cp-vcard:hover { transform:translateY(-2px); }
.cp-vcard-acts { opacity:0; transition:opacity .15s; }
.cp-vcard:hover .cp-vcard-acts { opacity:1; }

.cp-chip { transition:transform .14s,border-color .14s,background .14s; cursor:pointer; position:relative; }
.cp-chip:hover { transform:translateY(-2px); }
.cp-chip-acts { opacity:0; transition:opacity .15s; }
.cp-chip:hover .cp-chip-acts { opacity:1; }

.cp-arow { transition:background .12s; cursor:pointer; }
.cp-arow:hover { background:rgba(255,255,255,0.04) !important; }
.cp-arow-acts { opacity:0; transition:opacity .15s; }
.cp-arow:hover .cp-arow-acts { opacity:1; }

.cp-ibtn { transition:background .12s,color .12s; cursor:pointer; }
.cp-ibtn:hover { background:rgba(255,255,255,0.15) !important; color:#e2e8f0 !important; }
.cp-ibtn.danger:hover { background:rgba(239,68,68,0.22) !important; color:#f87171 !important; }

.cp-apply-btn { transition:transform .18s,box-shadow .18s; }
.cp-apply-btn:hover { transform:translateY(-2px); box-shadow:0 14px 36px rgba(37,99,235,0.56) !important; }
.cp-apply-btn:active { transform:translateY(0) !important; }

.cp-admin-tog { transition:all .15s; cursor:pointer; }
.cp-admin-tog:hover { opacity:.85; }

.cp-add-btn { transition:all .15s; cursor:pointer; }
.cp-add-btn:hover { background:rgba(245,158,11,0.2) !important; border-color:rgba(245,158,11,0.5) !important; color:#fbbf24 !important; }

.cp-save-btn  { transition:all .15s; cursor:pointer; }
.cp-save-btn:hover  { filter:brightness(1.1); }
.cp-cancel-btn { transition:all .15s; cursor:pointer; }
.cp-cancel-btn:hover { background:rgba(255,255,255,0.08) !important; color:#e2e8f0 !important; }

.cp-input-focus {
  background:rgba(255,255,255,0.08) !important;
  border-color:rgba(59,130,246,0.6) !important;
  color:#e8ecf1 !important;
  outline:none;
}
`;

// ─── DEFAULTS ──────────────────────────────────────────────────────────────────
const INIT_VEICULOS = [
  { id:'carro',      emoji:'🚗', label:'Carro de Passeio',   bandeirada:120, kmExc:4.50, franquia:20, consumo:6.0 },
  { id:'suv',        emoji:'🚙', label:'SUV / Pickup',        bandeirada:160, kmExc:5.80, franquia:20, consumo:6.0 },
  { id:'moto',       emoji:'🏍️', label:'Motocicleta',         bandeirada:80,  kmExc:3.00, franquia:15, consumo:7.0 },
  { id:'van',        emoji:'🚐', label:'Van / Utilitário',    bandeirada:190, kmExc:6.50, franquia:20, consumo:5.5 },
  { id:'cam_leve',   emoji:'🚛', label:'Cam. Leve (≤ 5t)',   bandeirada:240, kmExc:8.00, franquia:20, consumo:5.0 },
  { id:'cam_pesado', emoji:'🚚', label:'Cam. Pesado (> 5t)', bandeirada:380, kmExc:12.0, franquia:20, consumo:4.5 },
];
const INIT_CLIENTES = [
  { id:'particular',  emoji:'👤', label:'Particular',           mult:1.20 },
  { id:'seguradora',  emoji:'🏢', label:'Seguradora / Assist.', mult:0.88 },
  { id:'corporativo', emoji:'🏭', label:'Corporativo',          mult:1.00 },
];
const INIT_ADICIONAIS = [
  { id:'destombamento', emoji:'↕️',  label:'Destombamento',           valor:280 },
  { id:'subsolo',       emoji:'🏗️',  label:'Subsolo / Garagem Baixa', valor:180 },
  { id:'patins',        emoji:'🛞',  label:'Patins (rodas travadas)',  valor:150 },
  { id:'resgate',       emoji:'⛰️',  label:'Resgate em Ribanceira',   valor:400 },
  { id:'noturno',       emoji:'🌙',  label:'Adicional Noturno 22–06h',valor:100 },
  { id:'feriado',       emoji:'📅',  label:'Fim de Semana / Feriado', valor:80  },
  { id:'carga',         emoji:'🛡️',  label:'Seguro Carga (RCF-DC)',   valor:60  },
];

// ─── UTILS ─────────────────────────────────────────────────────────────────────
const fmt = v => Number(v || 0).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
const uid  = () => `_${Date.now().toString(36)}${Math.random().toString(36).slice(2,5)}`;

// ─── COUNTER ANIMADO ───────────────────────────────────────────────────────────
function Counter({ value }) {
  const [disp, setDisp] = useState(value);
  const prev = useRef(value);
  const raf  = useRef(null);
  useEffect(() => {
    const from = prev.current, to = value;
    prev.current = to;
    if (Math.abs(to - from) < 0.5) { setDisp(to); return; }
    cancelAnimationFrame(raf.current);
    const dur = 480, t0 = performance.now();
    const tick = t => {
      const p = Math.min((t - t0) / dur, 1);
      const e = p < .5 ? 4*p**3 : 1 - (-2*p+2)**3/2;
      setDisp(from + (to - from) * e);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [value]);
  return <>{fmt(disp)}</>;
}

// ─── SLIDER ────────────────────────────────────────────────────────────────────
function PSlider({ value, min, max, step, onChange, color='#f59e0b', glow }) {
  const pct = `${((value - min) / (max - min)) * 100}%`;
  const gl  = glow || `${color}30`;
  return (
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(Number(e.target.value))}
      className="cp-slider" style={{ '--sc':color, '--sp':pct, '--sg':gl }} />
  );
}

// ─── ICON BUTTON ───────────────────────────────────────────────────────────────
function IBtn({ onClick, danger=false, title, children }) {
  return (
    <button type="button" title={title}
      onClick={e => { e.stopPropagation(); onClick && onClick(); }}
      className={`cp-ibtn${danger?' danger':''}`}
      style={{ width:26, height:26, borderRadius:6, border:'none', padding:0, background:'rgba(255,255,255,0.08)', color:'#64748b', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, flexShrink:0 }}>
      {children}
    </button>
  );
}

// ─── SECTION HEADER ────────────────────────────────────────────────────────────
function SectionHead({ icon, label, adminMode, isAdmin, onAdd }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
      <div style={{ display:'flex', alignItems:'center', gap:9 }}>
        <span style={{ fontSize:17 }}>{icon}</span>
        <span style={{ fontFamily:"'Syne',sans-serif", fontSize:'0.82rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.07em', color:'#c8d5e8' }}>
          {label}
        </span>
      </div>
      {adminMode && isAdmin && (
        <button type="button" onClick={onAdd} className="cp-add-btn"
          style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 12px', borderRadius:7, border:'1px dashed rgba(245,158,11,0.3)', background:'transparent', color:'#92a3b8', fontSize:'0.72rem', fontWeight:700 }}>
          <FaPlus size={9} /> Adicionar
        </button>
      )}
    </div>
  );
}

// ─── ESTILOS DOS FORMULÁRIOS ───────────────────────────────────────────────────
const S_INPUT = {
  width:'100%', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)',
  borderRadius:7, padding:'8px 10px', color:'#e2e8f0', fontSize:'0.82rem', outline:'none',
  fontFamily:'inherit', transition:'border-color .15s,background .15s',
};
const S_LABEL = { display:'block', fontSize:'0.65rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#475569', marginBottom:5 };
const S_FORM  = { background:'rgba(245,158,11,0.07)', border:'1px solid rgba(245,158,11,0.22)', borderRadius:12, padding:'16px 18px', marginTop:10 };
const S_FTITLE= { fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'#f59e0b', marginBottom:13 };
const S_FBROW = { display:'flex', gap:9, marginTop:13, justifyContent:'flex-end' };

function FormCancelBtn({ onClick }) {
  return <button type="button" onClick={onClick} className="cp-cancel-btn" style={{ padding:'7px 15px', borderRadius:7, border:'1px solid rgba(255,255,255,0.11)', background:'transparent', color:'#64748b', fontSize:'0.78rem', fontWeight:600 }}>Cancelar</button>;
}
function FormSaveBtn({ onClick }) {
  return <button type="button" onClick={onClick} className="cp-save-btn" style={{ padding:'7px 18px', borderRadius:7, border:'none', background:'#f59e0b', color:'#000', fontSize:'0.78rem', fontWeight:800 }}>Salvar</button>;
}

// ─── VEICULO EDIT FORM ─────────────────────────────────────────────────────────
function VeiculoEditForm({ id, initial, onSave, onCancel }) {
  const [d, setD] = useState(initial || { emoji:'🚗', label:'', bandeirada:150, kmExc:5.0, franquia:20, consumo:6.0 });
  const fields = [
    { k:'emoji',      label:'Emoji',        w:'58px', type:'text'   },
    { k:'label',      label:'Nome',         w:'1fr',  type:'text'   },
    { k:'bandeirada', label:'Bandeirada',   w:'92px', type:'number', mono:true },
    { k:'kmExc',      label:'R$/km exc',    w:'82px', type:'number', mono:true },
    { k:'franquia',   label:'Franquia km',  w:'80px', type:'number', mono:true },
    { k:'consumo',    label:'km/L',         w:'66px', type:'number', mono:true },
  ];
  return (
    <div className="cp-form-anim" style={S_FORM}>
      <div style={S_FTITLE}>{id==='__new__' ? '➕ Novo Tipo de Veículo' : '✏️ Editar Veículo'}</div>
      <div style={{ display:'grid', gridTemplateColumns:fields.map(f=>f.w).join(' '), gap:9, alignItems:'end' }}>
        {fields.map(({ k, label, type, mono }) => (
          <div key={k}>
            <label style={S_LABEL}>{label}</label>
            <input className="cp-input-focus" type={type} value={d[k]}
              onChange={e => setD(p => ({ ...p, [k]: type==='number' ? Number(e.target.value)||0 : e.target.value }))}
              style={{ ...S_INPUT, fontFamily: mono ? "'DM Mono',monospace" : 'inherit' }} />
          </div>
        ))}
      </div>
      <div style={S_FBROW}>
        <FormCancelBtn onClick={onCancel} />
        <FormSaveBtn onClick={() => d.label.trim() && onSave(id, d)} />
      </div>
    </div>
  );
}

// ─── CLIENTE EDIT FORM ─────────────────────────────────────────────────────────
function ClienteEditForm({ id, initial, onSave, onCancel }) {
  const [d, setD] = useState(initial || { emoji:'👤', label:'', mult:1.0 });
  const pct = Math.round((d.mult - 1) * 100);
  return (
    <div className="cp-form-anim" style={{ ...S_FORM, marginTop:12 }}>
      <div style={S_FTITLE}>{id==='__new__' ? '➕ Nova Origem de Cliente' : '✏️ Editar Origem'}</div>
      <div style={{ display:'grid', gridTemplateColumns:'58px 1fr 108px', gap:9, alignItems:'end' }}>
        <div>
          <label style={S_LABEL}>Emoji</label>
          <input className="cp-input-focus" type="text" value={d.emoji} onChange={e=>setD(p=>({...p,emoji:e.target.value}))} style={S_INPUT} />
        </div>
        <div>
          <label style={S_LABEL}>Nome</label>
          <input className="cp-input-focus" type="text" value={d.label} onChange={e=>setD(p=>({...p,label:e.target.value}))} style={S_INPUT} />
        </div>
        <div>
          <label style={S_LABEL}>Ajuste (%)</label>
          <input className="cp-input-focus" type="number" value={pct} step={1}
            onChange={e=>setD(p=>({...p,mult:1+(Number(e.target.value)||0)/100}))}
            style={{ ...S_INPUT, fontFamily:"'DM Mono',monospace" }} />
        </div>
      </div>
      <div style={S_FBROW}>
        <FormCancelBtn onClick={onCancel} />
        <FormSaveBtn onClick={() => d.label.trim() && onSave(id, d)} />
      </div>
    </div>
  );
}

// ─── ADICIONAL EDIT FORM ───────────────────────────────────────────────────────
function AdicionalEditForm({ id, initial, onSave, onCancel }) {
  const [d, setD] = useState(initial || { emoji:'⭐', label:'', valor:100 });
  return (
    <div className="cp-form-anim" style={S_FORM}>
      <div style={S_FTITLE}>{id==='__new__' ? '➕ Novo Serviço Adicional' : '✏️ Editar Serviço'}</div>
      <div style={{ display:'grid', gridTemplateColumns:'58px 1fr 105px', gap:9, alignItems:'end' }}>
        <div>
          <label style={S_LABEL}>Emoji</label>
          <input className="cp-input-focus" type="text" value={d.emoji} onChange={e=>setD(p=>({...p,emoji:e.target.value}))} style={S_INPUT} />
        </div>
        <div>
          <label style={S_LABEL}>Descrição</label>
          <input className="cp-input-focus" type="text" value={d.label} onChange={e=>setD(p=>({...p,label:e.target.value}))} style={S_INPUT} />
        </div>
        <div>
          <label style={S_LABEL}>Valor (R$)</label>
          <input className="cp-input-focus" type="number" value={d.valor}
            onChange={e=>setD(p=>({...p,valor:Number(e.target.value)||0}))}
            style={{ ...S_INPUT, fontFamily:"'DM Mono',monospace" }} />
        </div>
      </div>
      <div style={S_FBROW}>
        <FormCancelBtn onClick={onCancel} />
        <FormSaveBtn onClick={() => d.label.trim() && onSave(id, d)} />
      </div>
    </div>
  );
}

// ─── INPUT NUMÉRICO INLINE (para os sliders) ────────────────────────────────────
function NumInput({ value, onChange, color = '#f59e0b', step = 1, width = 70, prefix, suffix }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:3, marginLeft:8 }}
         onClick={e => e.stopPropagation()}>
      {prefix && <span style={{ fontSize:'0.65rem', color:'#344152' }}>{prefix}</span>}
      <input
        type="number" value={value} step={step} min={0}
        onChange={e => onChange(e.target.value)}
        style={{ width, background:`${color}14`, border:`1px solid ${color}40`,
          borderRadius:5, padding:'2px 6px', color, fontSize:'0.72rem',
          fontFamily:"'DM Mono',monospace", outline:'none', textAlign:'right' }} />
      {suffix && <span style={{ fontSize:'0.65rem', color:'#344152' }}>{suffix}</span>}
    </div>
  );
}

// ─── SLIDER ROW ────────────────────────────────────────────────────────────────
function SliderRow({ icon, label, display, value, min, max, step, onChange, color, glow, note, extra }) {
  return (
    <div style={{ marginBottom:20 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:9 }}>
        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
          <span style={{ fontSize:14 }}>{icon}</span>
          <span style={{ fontSize:'0.76rem', fontWeight:600, color:'#6b7280' }}>{label}</span>
          {extra}
        </div>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.8rem', fontWeight:500, color, background:`${color}18`, padding:'3px 11px', borderRadius:20 }}>{display}</span>
      </div>
      <PSlider value={value} min={min} max={max} step={step} onChange={onChange} color={color} glow={glow} />
      {note && <div style={{ fontSize:'0.69rem', color:'#4b5563', marginTop:5, lineHeight:1.45 }}>{note}</div>}
    </div>
  );
}

// ─── BREAKDOWN ROW ─────────────────────────────────────────────────────────────
function BRow({ label, value, color='#8694a8', bold=false }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontSize:'0.76rem', color:bold?'#c8d5e8':'#64748b', fontWeight:bold?700:400, maxWidth:'64%', lineHeight:1.35 }}>{label}</span>
      <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.76rem', color, fontWeight:bold?700:400, flexShrink:0, marginLeft:8 }}>{fmt(value)}</span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
//
// >>> INTEGRAÇÃO COM O SIMULADOR (v3.1) <<<
//   • Nova prop `kmInicial`: pré-preenche a distância vinda da rota do mapa.
//   • `onAplicar` agora envia (total, detalhe) — `detalhe` é o snapshot
//     completo do cálculo (veículo, adicionais, margem, etc.). Continua
//     100% retrocompatível: quem só lê o 1º argumento (total) segue funcionando.
// ──────────────────────────────────────────────────────────────────────────────
export default function CalculadoraPreco({ onClose, onAplicar, isAdmin, kmInicial }) {
  // ── Tabelas (carregadas da API)
  const [veiculos,    setVeiculos]    = useState(INIT_VEICULOS);
  const [clientes,    setClientes]    = useState(INIT_CLIENTES);
  const [adicionais,  setAdicionais]  = useState(INIT_ADICIONAIS);
  const [taxaEspera,  setTaxaEspera]  = useState(80);

  // ── Estado de carregamento / salvamento
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [saveStatus,    setSaveStatus]    = useState(null); // null | 'saving' | 'saved' | 'error'
  const configReady = useRef(false); // evita salvar antes de carregar

  // ── Modo admin
  const [adminMode,   setAdminMode]   = useState(false);
  const [editing,     setEditing]     = useState(null);   // { type, id } | null
  const [addingType,  setAddingType]  = useState(null);

  // ── Seleções e parâmetros
  const [tipoVeiculo,   setTipoVeiculo]   = useState('carro');
  const [tipoCliente,   setTipoCliente]   = useState('particular');
  // >>> usa a distância da rota (kmInicial) quando disponível <<<
  const [kmTotal,       setKmTotal]       = useState(
    () => (kmInicial && Number(kmInicial) > 0 ? Number(kmInicial) : 30)
  );
  const [horasEspera,   setHorasEspera]   = useState(0);
  const [precoDiesel,   setPrecoDiesel]   = useState(6.40);
  const [margemPct,     setMargemPct]     = useState(15);
  const [adicionaisSel, setAdicionaisSel] = useState({});

  // Font injection
  useEffect(() => {
    const id = 'cp-google-fonts';
    if (!document.getElementById(id)) {
      const l = document.createElement('link');
      l.id = id; l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&display=swap';
      document.head.appendChild(l);
    }
  }, []);

  // ── Se a rota mudar enquanto a calculadora está aberta, reflete o km ─────────
  useEffect(() => {
    if (kmInicial && Number(kmInicial) > 0) setKmTotal(Number(kmInicial));
  }, [kmInicial]);

  // ── Carregar configuração da API ao montar ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoadingConfig(true);
    api.get('/api/calculadora-config')
      .then(({ data }) => {
        if (cancelled) return;
        const cfg = data?.config;
        if (cfg) {
          if (Array.isArray(cfg.veiculos)   && cfg.veiculos.length)   setVeiculos(cfg.veiculos);
          if (Array.isArray(cfg.clientes)   && cfg.clientes.length)   setClientes(cfg.clientes);
          if (Array.isArray(cfg.adicionais) && cfg.adicionais.length) setAdicionais(cfg.adicionais);
          if (cfg.taxa_espera) setTaxaEspera(parseFloat(cfg.taxa_espera));
        }
      })
      .catch(() => { /* usa defaults */ })
      .finally(() => {
        if (!cancelled) {
          setLoadingConfig(false);
          configReady.current = true;
        }
      });
    return () => { cancelled = true; };
  }, []);

  // ── Salvar na API (chamado explicitamente após cada operação admin) ─────────
  const persistConfig = useCallback((v, c, a, taxa) => {
    if (!isAdmin || !configReady.current) return;
    setSaveStatus('saving');
    api.put('/api/calculadora-config', { veiculos:v, clientes:c, adicionais:a, taxa_espera:taxa })
      .then(() => {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2500);
      })
      .catch(() => {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(null), 3000);
      });
  }, [isAdmin]);

  // ── Derived
  const veiculo = useMemo(() => veiculos.find(v => v.id === tipoVeiculo) || veiculos[0], [veiculos, tipoVeiculo]);
  const cliente = useMemo(() => clientes.find(c => c.id === tipoCliente) || clientes[0], [clientes, tipoCliente]);

  const calc = useMemo(() => {
    if (!veiculo || !cliente) return { kmExc:0, valorKmExc:0, espera:0, adics:0, custoDiesel:0, litros:'0', subtotal:0, ajuste:0, margem:0, total:0, itensAdic:[] };
    const kmExc      = Math.max(0, kmTotal - veiculo.franquia);
    const valorKmExc = kmExc * veiculo.kmExc;
    const distancia  = veiculo.bandeirada + valorKmExc;
    const espera     = horasEspera * taxaEspera;
    const itensAdic  = adicionais.filter(a => adicionaisSel[a.id]);
    const adics      = itensAdic.reduce((s,a) => s + a.valor, 0);
    const litros     = kmTotal / veiculo.consumo;
    const custoDiesel= litros * precoDiesel;
    const subtotal   = distancia + espera + adics;
    const comCliente = subtotal * cliente.mult;
    const ajuste     = comCliente - subtotal;
    const margem     = comCliente * (margemPct / 100);
    const total      = comCliente + margem;
    return { kmExc, valorKmExc, espera, adics, custoDiesel, litros:litros.toFixed(1), subtotal, ajuste, margem, total, itensAdic };
  }, [veiculo, cliente, kmTotal, horasEspera, precoDiesel, margemPct, taxaEspera, adicionaisSel, adicionais]);

  // ── Snapshot completo enviado ao simulador ao "Aplicar" ─────────────────────
  const montarDetalhe = useCallback(() => ({
    origem:       'calculadora',
    veiculoId:    veiculo?.id,
    veiculoLabel: veiculo?.label,
    veiculoEmoji: veiculo?.emoji,
    clienteId:    cliente?.id,
    clienteLabel: cliente?.label,
    clienteMult:  cliente?.mult,
    km:           kmTotal,
    franquia:     veiculo?.franquia,
    bandeirada:   veiculo?.bandeirada,
    kmExc:        calc.kmExc,
    kmExcRate:    veiculo?.kmExc,
    valorKmExc:   calc.valorKmExc,
    horasEspera,
    taxaEspera,
    espera:       calc.espera,
    itensAdic:    calc.itensAdic.map(a => ({ id:a.id, label:a.label, emoji:a.emoji, valor:a.valor })),
    adics:        calc.adics,
    subtotal:     calc.subtotal,
    ajuste:       calc.ajuste,
    margemPct,
    margem:       calc.margem,
    custoDiesel:  calc.custoDiesel,
    total:        calc.total,
  }), [veiculo, cliente, kmTotal, horasEspera, taxaEspera, margemPct, calc]);

  const aplicar = useCallback(() => {
    // 1º arg = total (string, retrocompatível) · 2º arg = snapshot completo
    onAplicar(calc.total.toFixed(2), montarDetalhe());
  }, [onAplicar, calc.total, montarDetalhe]);

  // ── Edição direta dos parâmetros de cálculo (km, diesel, margem) ────────────
  const handleKmChange        = (val) => setKmTotal(Math.max(0, Number(val) || 0));
  const handleDieselChange    = (val) => setPrecoDiesel(Math.max(0, Number(val) || 0));
  const handleMargemPctChange = (val) => setMargemPct(Math.max(0, Number(val) || 0));

  // Digita o VALOR da margem em R$ e recalcula a porcentagem automaticamente
  const handleMargemValorChange = (val) => {
    const valor = Math.max(0, Number(val) || 0);
    const base  = calc.subtotal + calc.ajuste; // = preço já com ajuste do cliente (comCliente)
    if (base > 0) setMargemPct(Number(((valor / base) * 100).toFixed(2)));
  };

  // ── Veículo CRUD ────────────────────────────────────────────────────────────
  const saveVeiculo = useCallback((id, data) => {
    let next;
    if (id === '__new__') next = [...veiculos, { id:uid(), ...data }];
    else next = veiculos.map(v => v.id === id ? { ...v, ...data } : v);
    setVeiculos(next);
    setEditing(null); setAddingType(null);
    persistConfig(next, clientes, adicionais, taxaEspera);
  }, [veiculos, clientes, adicionais, taxaEspera, persistConfig]);

  const deleteVeiculo = useCallback((id) => {
    const next = veiculos.filter(v => v.id !== id);
    if (tipoVeiculo === id) setTipoVeiculo(next[0]?.id || '');
    setVeiculos(next);
    setEditing(e => e?.id === id ? null : e);
    persistConfig(next, clientes, adicionais, taxaEspera);
  }, [veiculos, clientes, adicionais, taxaEspera, tipoVeiculo, persistConfig]);

  // ── Cliente CRUD ────────────────────────────────────────────────────────────
  const saveCliente = useCallback((id, data) => {
    let next;
    if (id === '__new__') next = [...clientes, { id:uid(), ...data }];
    else next = clientes.map(c => c.id === id ? { ...c, ...data } : c);
    setClientes(next);
    setEditing(null); setAddingType(null);
    persistConfig(veiculos, next, adicionais, taxaEspera);
  }, [veiculos, clientes, adicionais, taxaEspera, persistConfig]);

  const deleteCliente = useCallback((id) => {
    const next = clientes.filter(c => c.id !== id);
    if (tipoCliente === id) setTipoCliente(next[0]?.id || '');
    setClientes(next);
    setEditing(e => e?.id === id ? null : e);
    persistConfig(veiculos, next, adicionais, taxaEspera);
  }, [veiculos, clientes, adicionais, taxaEspera, tipoCliente, persistConfig]);

  // ── Adicional CRUD ──────────────────────────────────────────────────────────
  const saveAdicional = useCallback((id, data) => {
    let next;
    if (id === '__new__') next = [...adicionais, { id:uid(), ...data }];
    else next = adicionais.map(a => a.id === id ? { ...a, ...data } : a);
    setAdicionais(next);
    setEditing(null); setAddingType(null);
    persistConfig(veiculos, clientes, next, taxaEspera);
  }, [veiculos, clientes, adicionais, taxaEspera, persistConfig]);

  const deleteAdicional = useCallback((id) => {
    const next = adicionais.filter(a => a.id !== id);
    setAdicionaisSel(p => { const n={...p}; delete n[id]; return n; });
    setAdicionais(next);
    setEditing(e => e?.id === id ? null : e);
    persistConfig(veiculos, clientes, next, taxaEspera);
  }, [veiculos, clientes, adicionais, taxaEspera, persistConfig]);

  // ── Taxa de espera (admin) ──────────────────────────────────────────────────
  const handleTaxaChange = useCallback((val) => {
    const n = Math.max(0, Number(val) || 0);
    setTaxaEspera(n);
    persistConfig(veiculos, clientes, adicionais, n);
  }, [veiculos, clientes, adicionais, persistConfig]);

  const toggleAdmin = () => {
    if (!isAdmin) return;
    setAdminMode(m => !m);
    setEditing(null); setAddingType(null);
  };

  const startEditing = (type, id) => {
    setEditing(e => (e?.type===type && e?.id===id) ? null : { type, id });
    setAddingType(null);
  };
  const startAdding = (type) => {
    setEditing(null);
    setAddingType(at => at===type ? null : type);
  };

  // ── Save status label ───────────────────────────────────────────────────────
  const saveLabel = saveStatus === 'saving' ? '💾 Salvando…'
    : saveStatus === 'saved'  ? '✅ Salvo'
    : saveStatus === 'error'  ? '⚠️ Erro ao salvar'
    : null;

  return (
    <>
      <style>{CSS}</style>
      <div className="cp-root"
        style={{ position:'fixed', inset:0, zIndex:99999, background:'rgba(0,3,14,0.88)', display:'flex', alignItems:'center', justifyContent:'center', padding:14, backdropFilter:'blur(12px)' }}
        onClick={e => e.target===e.currentTarget && onClose()}>

        <div className="cp-modal-box" style={{ background:'#0c1322', borderRadius:22, width:'min(1260px,97vw)', height:'min(720px,93vh)', display:'flex', flexDirection:'column', overflow:'hidden', border:'1px solid rgba(255,255,255,0.06)', boxShadow:'0 50px 130px rgba(0,0,0,0.75),0 0 0 1px rgba(255,255,255,0.03)' }}>

          {/* ═══ HEADER ════════════════════════════════════════════════════ */}
          <header style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 28px', background:'linear-gradient(135deg,#07101e,#0c1929)', borderBottom:'1px solid rgba(255,255,255,0.06)', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:46, height:46, borderRadius:13, background:'rgba(59,130,246,0.14)', border:'1px solid rgba(59,130,246,0.24)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:21 }}>🧮</div>
              <div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'1.12rem', fontWeight:800, color:'#e8ecf1', letterSpacing:'-0.01em' }}>Calculadora de Preço</div>
                <div style={{ fontSize:'0.72rem', color:'#3d5068', marginTop:1 }}>Guincho & Assistência Veicular</div>
              </div>
            </div>

            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              {/* Save status */}
              {saveLabel && (
                <span style={{ fontSize:'0.72rem', color: saveStatus==='error'?'#f87171': saveStatus==='saved'?'#6ee7b7':'#94a3b8', background:'rgba(255,255,255,0.05)', padding:'5px 12px', borderRadius:20, border:'1px solid rgba(255,255,255,0.08)' }}>
                  {saveLabel}
                </span>
              )}

              {/* Admin toggle */}
              {isAdmin && (
                <button type="button" onClick={toggleAdmin} className="cp-admin-tog"
                  style={{ display:'flex', alignItems:'center', gap:7, padding:'7px 16px', borderRadius:20, background:adminMode?'rgba(245,158,11,0.14)':'rgba(255,255,255,0.05)', border:adminMode?'1px solid rgba(245,158,11,0.32)':'1px solid rgba(255,255,255,0.09)', color:adminMode?'#f59e0b':'#4a5568', fontSize:'0.74rem', fontWeight:700 }}>
                  <span style={{ fontSize:15 }}>{adminMode?'🔓':'🔒'}</span>
                  {adminMode?'Edição ativa':'Modo edição'}
                </button>
              )}

              <button type="button" onClick={onClose}
                style={{ width:34, height:34, borderRadius:9, background:'rgba(255,255,255,0.06)', border:'none', color:'#4a5568', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all .12s' }}
                onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.12)';e.currentTarget.style.color='#e2e8f0';}}
                onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.06)';e.currentTarget.style.color='#4a5568';}}>
                <FaTimes size={13}/>
              </button>
            </div>
          </header>

          {/* ═══ BODY ══════════════════════════════════════════════════════ */}
          {loadingConfig ? (
            <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:12, color:'#4a5568', fontSize:'0.9rem' }}>
              <FaSpinner style={{ animation:'spin 1s linear infinite' }} size={18}/> Carregando configuração…
            </div>
          ) : (
            <div style={{ display:'flex', flex:1, overflow:'hidden', minHeight:0 }}>

              {/* ── PAINEL ESQUERDO ─────────────────────────────────────── */}
              <div className="cp-scroll" style={{ width:'58%', padding:'24px 26px', borderRight:'1px solid rgba(255,255,255,0.045)', overflowY:'auto', overflowX:'hidden' }}>

                {/* ══ TIPO DE VEÍCULO ═════════════════════════════════════ */}
                <div style={{ marginBottom:30 }}>
                  <SectionHead icon="🚛" label="Tipo de Veículo" adminMode={adminMode} isAdmin={isAdmin} onAdd={()=>startAdding('veiculo')} />
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
                    {veiculos.map(v => {
                      const sel = tipoVeiculo === v.id;
                      const editingThis = editing?.type==='veiculo' && editing?.id===v.id;
                      return (
                        <div key={v.id} className="cp-vcard"
                          style={{ padding:'15px 14px', borderRadius:13, border:`1.5px solid ${sel?'#3b82f6':editingThis?'rgba(245,158,11,0.4)':'rgba(255,255,255,0.08)'}`, background:sel?'linear-gradient(140deg,#18365a,#112b4a)':editingThis?'rgba(245,158,11,0.07)':'#131d30', boxShadow:sel?'0 0 0 3px rgba(59,130,246,0.15)':'none' }}
                          onClick={() => {
                            if (adminMode) startEditing('veiculo', v.id);
                            else setTipoVeiculo(v.id);
                          }}>
                          {adminMode && (
                            <div className="cp-vcard-acts" style={{ position:'absolute', top:8, right:8, display:'flex', gap:4, zIndex:2 }}>
                              <IBtn onClick={()=>startEditing('veiculo',v.id)} title="Editar"><FaPencilAlt size={10}/></IBtn>
                              <IBtn danger onClick={()=>deleteVeiculo(v.id)} title="Excluir"><FaTrash size={10}/></IBtn>
                            </div>
                          )}
                          <div style={{ fontSize:32, lineHeight:1, marginBottom:8 }}>{v.emoji}</div>
                          <div style={{ fontSize:'0.8rem', fontWeight:700, color:sel?'#93c5fd':'#8a9bb0', lineHeight:1.3, marginBottom:9 }}>{v.label}</div>
                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            {[
                              { k:'Bandeirada', val:fmt(v.bandeirada) },
                              { k:'km excedente', val:`${fmt(v.kmExc)}/km` },
                              { k:'Franquia', val:`${v.franquia} km` },
                            ].map(({k,val})=>(
                              <div key={k} style={{ display:'flex', justifyContent:'space-between' }}>
                                <span style={{ fontSize:'0.65rem', color:sel?'#4a7ab5':'#2e3f52' }}>{k}</span>
                                <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.68rem', color:sel?'#bfdbfe':'#4a6070', fontWeight:500 }}>{val}</span>
                              </div>
                            ))}
                          </div>
                          {sel && !adminMode && (
                            <div style={{ position:'absolute', top:8, right:8, width:20, height:20, borderRadius:'50%', background:'#3b82f6', display:'flex', alignItems:'center', justifyContent:'center' }}>
                              <FaCheck size={9} color="white"/>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {editing?.type==='veiculo' && editing.id!=='__new__' && (
                    <VeiculoEditForm id={editing.id} initial={veiculos.find(v=>v.id===editing.id)} onSave={saveVeiculo} onCancel={()=>setEditing(null)} />
                  )}
                  {addingType==='veiculo' && (
                    <VeiculoEditForm id="__new__" initial={null} onSave={saveVeiculo} onCancel={()=>setAddingType(null)} />
                  )}
                </div>

                {/* ══ ORIGEM DO CLIENTE ═══════════════════════════════════ */}
                <div style={{ marginBottom:30 }}>
                  <SectionHead icon="👤" label="Origem do Cliente" adminMode={adminMode} isAdmin={isAdmin} onAdd={()=>startAdding('cliente')} />
                  <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                    {clientes.map(c => {
                      const sel = tipoCliente === c.id;
                      const isPlus = c.mult > 1.005, isMinus = c.mult < 0.995;
                      const mc = isPlus?'#10b981':isMinus?'#f87171':'#64748b';
                      const ml = c.mult===1?'base':`${isPlus?'+':''}${Math.round((c.mult-1)*100)}%`;
                      return (
                        <div key={c.id} className="cp-chip"
                          style={{ position:'relative', display:'flex', flexDirection:'column', alignItems:'center', gap:5, padding:'12px 20px', borderRadius:13, border:`1.5px solid ${sel?'#3b82f6':'rgba(255,255,255,0.09)'}`, background:sel?'rgba(59,130,246,0.12)':'#131d30', minWidth:108, boxShadow:sel?'0 0 0 3px rgba(59,130,246,0.1)':'none' }}
                          onClick={() => {
                            if (adminMode) startEditing('cliente', c.id);
                            else setTipoCliente(c.id);
                          }}>
                          {adminMode && (
                            <div className="cp-chip-acts" style={{ position:'absolute', top:5, right:5, display:'flex', gap:3 }}>
                              <IBtn onClick={()=>startEditing('cliente',c.id)} title="Editar"><FaPencilAlt size={9}/></IBtn>
                              <IBtn danger onClick={()=>deleteCliente(c.id)} title="Excluir"><FaTrash size={9}/></IBtn>
                            </div>
                          )}
                          <span style={{ fontSize:23 }}>{c.emoji}</span>
                          <span style={{ fontSize:'0.8rem', fontWeight:700, color:sel?'#93c5fd':'#7a8fa5', lineHeight:1 }}>{c.label}</span>
                          <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.7rem', fontWeight:700, color:mc, background:`${mc}18`, padding:'3px 9px', borderRadius:20 }}>{ml}</span>
                          {sel && !adminMode && (
                            <div style={{ position:'absolute', top:7, right:7, width:17, height:17, borderRadius:'50%', background:'#3b82f6', display:'flex', alignItems:'center', justifyContent:'center' }}>
                              <FaCheck size={8} color="white"/>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {editing?.type==='cliente' && (
                    <ClienteEditForm id={editing.id} initial={clientes.find(c=>c.id===editing.id)} onSave={saveCliente} onCancel={()=>setEditing(null)} />
                  )}
                  {addingType==='cliente' && (
                    <ClienteEditForm id="__new__" initial={null} onSave={saveCliente} onCancel={()=>setAddingType(null)} />
                  )}
                </div>

                {/* ══ SERVIÇOS ADICIONAIS ══════════════════════════════════ */}
                <div>
                  <SectionHead icon="🔧" label="Serviços Adicionais" adminMode={adminMode} isAdmin={isAdmin} onAdd={()=>startAdding('adicional')} />
                  <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                    {adicionais.map(a => {
                      const sel = !!adicionaisSel[a.id];
                      const editingThis = editing?.type==='adicional' && editing?.id===a.id;
                      return (
                        <div key={a.id}>
                          <div className="cp-arow"
                            style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 14px', borderRadius:10, border:`1px solid ${sel?'rgba(16,185,129,0.3)':editingThis?'rgba(245,158,11,0.28)':'rgba(255,255,255,0.06)'}`, background:sel?'rgba(16,185,129,0.07)':editingThis?'rgba(245,158,11,0.05)':'transparent' }}
                            onClick={() => {
                              if (adminMode) startEditing('adicional', a.id);
                              else setAdicionaisSel(p=>({...p,[a.id]:!p[a.id]}));
                            }}>
                            <span style={{ fontSize:20, flexShrink:0 }}>{a.emoji}</span>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:'0.8rem', fontWeight:600, color:sel?'#6ee7b7':'#7a8fa5', lineHeight:1.3 }}>{a.label}</div>
                              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.7rem', color:sel?'#10b981':'#344152', marginTop:2 }}>+ {fmt(a.valor)}</div>
                            </div>
                            {adminMode ? (
                              <div className="cp-arow-acts" style={{ display:'flex', gap:5 }}>
                                <IBtn onClick={()=>startEditing('adicional',a.id)} title="Editar"><FaPencilAlt size={10}/></IBtn>
                                <IBtn danger onClick={()=>deleteAdicional(a.id)} title="Excluir"><FaTrash size={10}/></IBtn>
                              </div>
                            ) : (
                              <div style={{ width:21, height:21, borderRadius:5, background:sel?'#10b981':'rgba(255,255,255,0.08)', border:sel?'none':'1px solid rgba(255,255,255,0.12)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all .12s' }}>
                                {sel && <FaCheck size={9} color="white"/>}
                              </div>
                            )}
                          </div>
                          {editingThis && (
                            <AdicionalEditForm id={a.id} initial={a} onSave={saveAdicional} onCancel={()=>setEditing(null)} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {addingType==='adicional' && (
                    <AdicionalEditForm id="__new__" initial={null} onSave={saveAdicional} onCancel={()=>setAddingType(null)} />
                  )}
                </div>

              </div>

              {/* ── PAINEL DIREITO ───────────────────────────────────────── */}
              <div style={{ flex:1, background:'#090f1c', display:'flex', flexDirection:'column', overflow:'hidden' }}>

                {/* Parâmetros */}
                <div style={{ padding:'22px 26px 18px', borderBottom:'1px solid rgba(255,255,255,0.05)', flexShrink:0 }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'0.72rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.09em', color:'#2563eb', marginBottom:20 }}>
                    ⚙️ Parâmetros
                  </div>

                  <SliderRow icon="📍" label="Distância Total"
                    value={kmTotal} min={1} max={1000} step={1} onChange={setKmTotal}
                    display={`${kmTotal} km`} color="#f59e0b" glow="rgba(245,158,11,0.22)"
                    extra={<NumInput value={kmTotal} onChange={handleKmChange} color="#f59e0b" suffix="km" />}
                    note={calc.kmExc > 0
                      ? <span style={{color:'#4b5563'}}>Franquia: <b style={{color:'#6b7280'}}>{veiculo?.franquia}km</b> · Excedente: <b style={{color:'#f59e0b'}}>{calc.kmExc}km × {fmt(veiculo?.kmExc)}/km = {fmt(calc.valorKmExc)}</b></span>
                      : <span style={{color:'#10b981'}}>✓ Dentro da franquia de {veiculo?.franquia}km</span>
                    }
                  />

                  <SliderRow icon="⏱️" label="Tempo de Espera"
                    value={horasEspera} min={0} max={8} step={0.5} onChange={setHorasEspera}
                    display={`${horasEspera}h`} color="#06b6d4" glow="rgba(6,182,212,0.22)"
                    note={horasEspera > 0 ? <span style={{color:'#4b5563'}}>Taxa: <b style={{color:'#06b6d4'}}>{fmt(taxaEspera)}/h</b> · Custo: {fmt(calc.espera)}</span> : null}
                    extra={adminMode && isAdmin ? (
                      <div style={{ display:'flex', alignItems:'center', gap:5, marginLeft:8 }}>
                        <span style={{ fontSize:'0.65rem', color:'#344152' }}>taxa:</span>
                        <input type="number" value={taxaEspera}
                          onChange={e=>handleTaxaChange(e.target.value)}
                          onClick={e=>e.stopPropagation()}
                          style={{ width:54, background:'rgba(6,182,212,0.1)', border:'1px solid rgba(6,182,212,0.3)', borderRadius:5, padding:'2px 6px', color:'#06b6d4', fontSize:'0.7rem', fontFamily:"'DM Mono',monospace", outline:'none' }} />
                        <span style={{ fontSize:'0.65rem', color:'#344152' }}>/h</span>
                      </div>
                    ) : null}
                  />

                  <SliderRow icon="⛽" label="Preço do Diesel"
                    value={precoDiesel} min={4} max={20} step={0.05} onChange={setPrecoDiesel}
                    display={`R$ ${precoDiesel.toFixed(2)}`} color="#f97316" glow="rgba(249,115,22,0.22)"
                    extra={<NumInput value={precoDiesel} onChange={handleDieselChange} color="#f97316" step={0.05} prefix="R$" width={66} />}
                    note={<span style={{color:'#4b5563'}}>~{calc.litros}L · custo: <b style={{color:'#f97316'}}>{fmt(calc.custoDiesel)}</b></span>}
                  />

                  <SliderRow icon="📈" label="Margem do Operador"
                    value={margemPct} min={0} max={100} step={1} onChange={setMargemPct}
                    display={`${margemPct}%`} color="#10b981" glow="rgba(16,185,129,0.22)"
                    extra={
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <NumInput value={margemPct} onChange={handleMargemPctChange} color="#10b981" suffix="%" width={54} />
                        <NumInput value={Number(calc.margem.toFixed(2))} onChange={handleMargemValorChange} color="#10b981" prefix="R$" width={74} />
                      </div>
                    }
                    note={margemPct>0 ? <span style={{color:'#4b5563'}}>Valor da margem: <b style={{color:'#10b981'}}>{fmt(calc.margem)}</b></span> : null}
                  />
                </div>

                {/* Resumo */}
                <div className="cp-scroll" style={{ flex:1, padding:'18px 26px 12px', overflowY:'auto' }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'0.7rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.09em', color:'#3d5068', marginBottom:14 }}>
                    📋 Resumo do Orçamento
                  </div>

                  <BRow label={`Bandeirada ${veiculo?.emoji} ${veiculo?.label}`} value={veiculo?.bandeirada} />
                  {calc.kmExc > 0 && <BRow label={`KM excedente (${calc.kmExc}km × ${fmt(veiculo?.kmExc)}/km)`} value={calc.valorKmExc} color="#f59e0b" />}
                  {calc.espera > 0 && <BRow label={`Espera (${horasEspera}h × ${fmt(taxaEspera)}/h)`} value={calc.espera} color="#06b6d4" />}
                  {calc.itensAdic.map(a => <BRow key={a.id} label={`${a.emoji} ${a.label}`} value={a.valor} color="#a78bfa" />)}

                  <div style={{ borderTop:'1px solid rgba(255,255,255,0.09)', margin:'8px 0' }} />
                  <BRow label="Subtotal" value={calc.subtotal} bold />
                  {Math.abs(calc.ajuste) > 0.5 && (
                    <BRow label={`Ajuste ${cliente?.label} (×${cliente?.mult.toFixed(2)})`} value={calc.ajuste} color={calc.ajuste>=0?'#10b981':'#f87171'} />
                  )}
                  {margemPct > 0 && <BRow label={`Margem operador (${margemPct}%)`} value={calc.margem} color="#60a5fa" />}

                  <div style={{ borderTop:'1px solid rgba(255,255,255,0.12)', marginTop:9, paddingTop:11 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                      <span style={{ fontFamily:"'Syne',sans-serif", fontSize:'0.84rem', fontWeight:800, color:'#c8d5e8', letterSpacing:'0.02em' }}>TOTAL DA OS</span>
                      <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'1.22rem', fontWeight:500, color:'#f1f5f9', letterSpacing:'-0.02em' }}>
                        <Counter value={calc.total} />
                      </span>
                    </div>
                    <div style={{ fontSize:'0.65rem', color:'#243040', marginTop:6 }}>
                      ⛽ Diesel: {fmt(calc.custoDiesel)} · {calc.total>0?((calc.custoDiesel/calc.total)*100).toFixed(1):0}% do faturamento
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div style={{ padding:'15px 24px', borderTop:'1px solid rgba(255,255,255,0.04)', flexShrink:0 }}>
                  <button type="button" className="cp-apply-btn"
                    onClick={aplicar}
                    style={{ width:'100%', padding:'15px 20px', background:'linear-gradient(135deg,#1d4ed8,#2563eb)', color:'#fff', border:'none', borderRadius:13, fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:'0.94rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:11, boxShadow:'0 5px 22px rgba(37,99,235,0.42)', letterSpacing:'0.01em' }}>
                    <FaArrowRight size={14} />
                    Aplicar {fmt(calc.total)} como valor da OS
                  </button>
                </div>

              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}